// filepath: apps/web/src/admin/positions/detail.tsx
//
// ISS-516 — PositionDetailScreen.
//
// Read-only detail view. Renders the BA-corrected Go-aligned
// fields (title, job_code, status with Badge, organization_unit_id
// short-id, location_id short-id, closed_at, created_at,
// row_version, created_by_subject) + an Archive button that
// POSTs /api/directory/positions/{id}/close (the SPA-facing
// verb is "Archive"; the Go verb is "close" per BA spec).
//
// INV-7: the Archive button carries row_version; the server
// returns 409 row_version_mismatch on stale submission and the
// SPA surfaces a StaleVersionError (reload to fetch fresh
// row_version).
//
// INV-9: no dangerouslySetInnerHTML; every value flows through
// JSX text / design-system primitives.

import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { formatDatePattern } from "@/i18n/format";
import { Card } from "@/design-system/Card";
import { Badge } from "@/design-system/Badge";
import type { BadgeTone } from "@/design-system/Badge";
import { Button } from "@/design-system/Button";
import { Banner } from "@/design-system/Banner";
import { StaleVersionError } from "@/design-system/error-states/StaleVersionError";
import { PermissionDenied } from "@/design-system/error-states/PermissionDenied";
import { ApiError } from "@/definitions/types";
import { navigate } from "@/features/cabinet/usePathname";
import {
  archivePosition,
  getPosition,
  newIdempotencyKey,
  type Position,
  type ArchivePositionInput,
} from "./api";

function handleSpaNavigate(
  e: React.MouseEvent<HTMLAnchorElement>,
  href: string,
): void {
  if (e.button !== 0 || e.metaKey || e.altKey || e.ctrlKey || e.shiftKey) {
    return;
  }
  e.preventDefault();
  navigate(href);
}

function shortId(uuid: string | null): string {
  if (!uuid) return "—";
  return uuid.slice(0, 8);
}

function statusTone(status: "open" | "filled" | "closed"): BadgeTone {
  switch (status) {
    case "open":
      return "neutral";
    case "filled":
      return "success";
    case "closed":
      return "warning";
  }
}

function isPermissionDenied(e: unknown): boolean {
  if (e instanceof ApiError) return e.status === 403 || e.status === 404;
  return false;
}

export interface PositionDetailScreenProps {
  id?: string;
  loader?: (id: string) => Promise<Position | null>;
  archiver?: (
    id: string,
    input: ArchivePositionInput,
    key: string,
  ) => Promise<Position>;
  orgUnitName?: (id: string | null) => string | undefined;
  locationName?: (id: string | null) => string | undefined;
}

export function PositionDetailScreen({
  id = "",
  loader,
  archiver,
  orgUnitName,
  locationName,
}: PositionDetailScreenProps = {}): React.ReactElement {
  const { t, locale } = useTranslation();
  const [row, setRow] = useState<Position | null>(null);
  const [loading, setLoading] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [stale, setStale] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    setStale(false);
    setPermissionDenied(false);
    try {
      const l = loader ?? ((cid: string) => getPosition(cid));
      const cp = await l(id);
      setRow(cp);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (isPermissionDenied(e)) {
        setPermissionDenied(true);
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }, [id, loader]);

  useEffect(() => {
    void load();
  }, [load]);

  const onArchive = useCallback(async () => {
    if (!row) return;
    setArchiving(true);
    setError(null);
    setStale(false);
    setPermissionDenied(false);
    const key = newIdempotencyKey();
    try {
      const a = archiver ?? archivePosition;
      await a(row.id, { row_version: row.row_version }, key);
      await load();
    } catch (e2) {
      const msg = e2 instanceof Error ? e2.message : String(e2);
      if (isPermissionDenied(e2)) {
        setPermissionDenied(true);
      } else if (
        e2 instanceof ApiError &&
        e2.status === 409 &&
        e2.code === "row_version_mismatch"
      ) {
        setStale(true);
      } else {
        setError(msg);
      }
    } finally {
      setArchiving(false);
    }
  }, [row, archiver, load]);

  if (loading) {
    return (
      <p data-testid="position-detail-loading">
        {t("directory.positions.state.loading", "Loading position…")}
      </p>
    );
  }
  if (permissionDenied) {
    return (
      <div data-testid="position-detail-permission-denied">
        <PermissionDenied />
      </div>
    );
  }
  if (error && !row) {
    return (
      <p role="alert" data-testid="position-detail-error">
        {t("directory.positions.error", "Error: {{message}}", {
          message: error,
        })}
      </p>
    );
  }
  if (!row) {
    return (
      <p data-testid="position-detail-not-found">
        {t("directory.positions.not_found", "Position not found")}
      </p>
    );
  }

  return (
    <section data-testid="position-detail-screen">
      <header>
        <h1 id="position-detail-title">{row.title}</h1>
      </header>
      {stale ? (
        <StaleVersionError
          entityName={row.title}
          onReload={() => void load()}
          data-testid="position-detail-stale"
        />
      ) : null}
      {error ? (
        <Banner tone="danger" data-testid="position-detail-error">
          {error}
        </Banner>
      ) : null}
      <Card>
        <dl>
          <dt>{t("directory.positions.field.job_code", "Job code")}</dt>
          <dd data-testid="position-detail-job-code">{row.job_code ?? "—"}</dd>
          <dt>{t("directory.positions.field.status", "Status")}</dt>
          <dd>
            <Badge
              tone={statusTone(row.status)}
              data-testid="position-detail-status"
            >
              {t(`directory.positions.status.${row.status}`, row.status)}
            </Badge>
          </dd>
          <dt>
            {t(
              "directory.positions.field.organization_unit",
              "Organization unit",
            )}
          </dt>
          <dd data-testid="position-detail-org-unit">
            {row.organization_unit_id
              ? (orgUnitName?.(row.organization_unit_id) ??
                shortId(row.organization_unit_id))
              : "—"}
          </dd>
          <dt>{t("directory.positions.field.location", "Location")}</dt>
          <dd data-testid="position-detail-location">
            {row.location_id
              ? (locationName?.(row.location_id) ?? shortId(row.location_id))
              : "—"}
          </dd>
          {row.filled_by_person_id ? (
            <>
              <dt>
                {t(
                  "directory.positions.field.filled_by_person_id",
                  "Filled by",
                )}
              </dt>
              <dd>{shortId(row.filled_by_person_id)}</dd>
            </>
          ) : null}
          {row.closed_at ? (
            <>
              <dt>{t("directory.positions.field.closed_at", "Closed at")}</dt>
              <dd>{formatDatePattern(new Date(row.closed_at), locale)}</dd>
            </>
          ) : null}
          <dt>{t("directory.positions.field.created_at", "Created at")}</dt>
          <dd>{formatDatePattern(new Date(row.created_at), locale)}</dd>
          <dt>{t("directory.positions.field.created_by", "Created by")}</dt>
          <dd>{shortId(row.created_by_subject)}</dd>
          <dt>{t("directory.positions.field.row_version", "Row version")}</dt>
          <dd data-testid="position-detail-row-version">{row.row_version}</dd>
        </dl>
        <div className="position-detail-actions">
          <a
            href={`/admin/positions/${encodeURIComponent(row.id)}/edit`}
            onClick={(e) =>
              handleSpaNavigate(
                e,
                `/admin/positions/${encodeURIComponent(row.id)}/edit`,
              )
            }
            data-testid="position-detail-edit-link"
          >
            {t("directory.positions.action.edit", "Edit")}
          </a>
          {row.status !== "closed" ? (
            <Button
              type="button"
              variant="danger"
              onClick={() => void onArchive()}
              disabled={archiving}
              data-testid="position-detail-archive"
            >
              {archiving
                ? t("directory.positions.action.archiving", "Archiving…")
                : t("directory.positions.action.archive", "Archive")}
            </Button>
          ) : null}
        </div>
      </Card>
    </section>
  );
}

export default PositionDetailScreen;
